import React, { useState, useEffect } from 'react';
import StatCard from '../components/StatCard';
import TrasladoDetalleModal from '../components/TrasladoDetalleModal';
import { getProducts } from '../services/productService';
import { getRecentTransfers, getCriticalStock } from '../services/transferService';
import { useAuth } from '../context/AuthContext';

export default function Dashboard() {
  const [products, setProducts] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [critical, setCritical] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTransfer, setSelectedTransfer] = useState(null);
  const { user } = useAuth();

  useEffect(() => {
    Promise.all([
      getProducts().catch(() => []),
      getRecentTransfers().catch(() => []),
      getCriticalStock().catch(() => []),
    ]).then(([prod, trans, crit]) => {
      setProducts(Array.isArray(prod) ? prod : []);
      setTransfers(Array.isArray(trans) ? trans : []);
      setCritical(Array.isArray(crit) ? crit : []);
    }).finally(() => setLoading(false));
  }, []);

  const total = products.length;
  const activos = products.filter(p => p.state).length;
  const totalTraslados = transfers.length;
  const totalCriticos = critical.length;

  const spinner = (
    <div style={{ padding: 32, textAlign: 'center', color: 'var(--orig-text-muted)' }}>
      <div style={{ fontSize: 24, marginBottom: 8 }}>
        <i className="bi bi-arrow-repeat" style={{ animation: 'spin 1s linear infinite', display: 'inline-block' }} />
      </div>
      Cargando...
    </div>
  );

  const empty = (icon, text) => (
    <div style={{ padding: 32, textAlign: 'center', color: 'var(--orig-text-muted)' }}>
      <i className={`bi ${icon}`} style={{ fontSize: 32, display: 'block', marginBottom: 8 }} />
      {text}
    </div>
  );

  return (
    <div className="page-fade" style={{ padding: 24 }}>
      <div style={{ marginBottom: 22 }}>
        <p style={{ fontSize: 12, color: 'var(--orig-text-muted)', marginBottom: 3 }}>
          Bienvenido, {user?.name} {user?.lastName}
        </p>
        <h5 style={{
          fontWeight: 800, color: 'var(--orig-text)',
          fontFamily: 'var(--orig-font-display)', letterSpacing: 0.5, fontSize: 20,
        }}>
          Resumen del Inventario
        </h5>
      </div>

      <div className="row g-3 mb-4">
        <div className="col-6 col-md-3">
          <StatCard color="green" label="Total Productos" value={loading ? '—' : total} sub="En catalogo" icon="bi-box-seam" />
        </div>
        <div className="col-6 col-md-3">
          <StatCard color="teal" label="Productos Activos" value={loading ? '—' : activos} sub="Estado activo" icon="bi-check-circle" />
        </div>
        <div className="col-6 col-md-3">
          <StatCard color="amber" label="Traslados Recientes" value={loading ? '—' : totalTraslados} sub="Ultimos 15" icon="bi-arrow-left-right" />
        </div>
        <div className="col-6 col-md-3">
          <StatCard color="red" label="Stock Critico" value={loading ? '—' : totalCriticos} sub="Bajo minimo" icon="bi-exclamation-triangle" />
        </div>
      </div>

      <div className="row g-3">
        {/* Ultimos Traslados */}
        <div className="col-12 col-lg-6">
          <div className="section-card" style={{ marginBottom: 0, height: '100%' }}>
            <div className="section-header">
              <h6>
                <i className="bi bi-arrow-left-right" style={{ color: 'var(--orig-green)' }} />
                Ultimos Traslados
              </h6>
              {loading
                ? <span className="badge-orig badge-amber">Cargando...</span>
                : <span className="badge-orig badge-green">{totalTraslados} traslados</span>
              }
            </div>
            <div style={{ overflowX: 'auto', maxHeight: 480, overflowY: 'auto' }}>
              {loading ? spinner : transfers.length === 0 ? empty('bi-inbox', 'Sin traslados registrados') : (
                <table className="orig-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Fecha</th>
                      <th>Origen</th>
                      <th>Destino</th>
                      <th>Productos</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {transfers.map(t => (
                      <tr key={t.id}>
                        <td style={{ color: 'var(--orig-text-muted)', fontSize: 12 }}>{t.id}</td>
                        <td style={{ fontSize: 12.5 }}>{t.date?.slice(0, 10)}</td>
                        <td>
                          <span style={{
                            background: 'var(--orig-teal-dim)', color: 'var(--orig-teal-light)',
                            padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600,
                          }}>
                            {t.branchOrigin?.name || '—'}
                          </span>
                        </td>
                        <td>
                          <span style={{
                            background: 'var(--orig-green-dim)', color: 'var(--orig-green-bright)',
                            padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600,
                          }}>
                            {t.branchDest?.name || '—'}
                          </span>
                        </td>
                        <td style={{ fontSize: 12.5, fontWeight: 500 }}>
                          {t.details?.length || 0} items
                        </td>
                        <td>
                          <button
                            className="btn-orig btn-orig-ghost"
                            style={{ padding: '4px 10px', fontSize: 11 }}
                            onClick={() => setSelectedTransfer(t)}
                          >
                            <i className="bi bi-eye" /> Ver
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>

        {/* Stock Critico */}
        <div className="col-12 col-lg-6">
          <div className="section-card" style={{ marginBottom: 0, height: '100%' }}>
            <div className="section-header">
              <h6>
                <i className="bi bi-exclamation-triangle" style={{ color: 'var(--orig-red)' }} />
                Stock Critico
              </h6>
              {loading
                ? <span className="badge-orig badge-amber">Cargando...</span>
                : <span className="badge-orig badge-red">{totalCriticos} alertas</span>
              }
            </div>
            <div style={{ overflowX: 'auto', maxHeight: 480, overflowY: 'auto' }}>
              {loading ? spinner : critical.length === 0 ? empty('bi-check-circle', 'Sin productos en estado critico') : (
                <table className="orig-table">
                  <thead>
                    <tr>
                      <th>Producto</th>
                      <th>Sucursal</th>
                      <th>Stock</th>
                      <th>Minimo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {critical.map((item, idx) => {
                      const pct = item.stockMin > 0 ? Math.round((item.totalStock / item.stockMin) * 100) : 0;
                      const isZero = item.totalStock === 0;
                      return (
                        <tr key={`${item.productId}-${item.branchId}-${idx}`}>
                          <td style={{ fontWeight: 500 }}>
                            {item.productName}
                            {item.packageType && (
                              <span style={{ fontSize: 10, color: 'var(--orig-text-muted)', marginLeft: 6 }}>
                                {item.packageType}
                              </span>
                            )}
                          </td>
                          <td>
                            <span style={{
                              background: 'var(--orig-green-dim)', color: 'var(--orig-green-bright)',
                              padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600,
                            }}>
                              {item.branchName}
                            </span>
                          </td>
                          <td style={{ fontWeight: 600, color: isZero ? 'var(--orig-red-light)' : 'var(--orig-amber-light)' }}>
                            {item.totalStock}
                          </td>
                          <td style={{ fontSize: 12.5, color: 'var(--orig-text-muted)' }}>
                            {item.stockMin}
                          </td>
                          {/* 
                          <td>
                            <span className={`badge-orig ${isZero ? 'badge-red' : 'badge-amber'}`}>
                              <i className="bi bi-circle-fill" style={{ fontSize: 6 }} />
                              {isZero ? 'Agotado' : `${pct}%`}
                            </span>
                          </td>
                          */}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </div>

      {selectedTransfer && (
        <TrasladoDetalleModal
          traslado={selectedTransfer}
          onClose={() => setSelectedTransfer(null)}
        />
      )}
    </div>
  );
}
